/* eslint class-methods-use-this: 0 */
import { IGridHelper } from './interfaces';
import { CustomCells } from '@components-clean/core/grid/custom-cell';
import { ChipProps, RAGPillColours } from '@components-clean/core/grid/custom-cell/chip-constants';
import { ColumnDefinition } from '@interfaces/central-monitoring';

enum ReviewLogColumns {
    // Although these aren't ragged columns I need to set them to true
    // So we get the benefit of the advanced filter
    PreviousXAxisValue = 'previous_x_axis',
    PreviousYAxisValue = 'previous_y_axis',
    PreviousYVolume = 'previous_y_volume',
    PreviousScore = 'previous_panel_score',
    CreatedBy = 'created_by',
    CreatedDate = 'created_date',
    Id = 'id',
    UpdatedBy = 'updated_by',
    ClosedDatePopulatedOn = 'closed_date_populated_on',
    RecordId = 'record_id',
    Owner = 'owner',
    Observation = 'observation',
    RootCause = 'root_cause',
    Comments = 'comments',
    Association = 'association',
    Panel = 'panel',
    PurpleCount = 'purple_count',
    Decision = 'decision',
    DataSet = 'dataset',
    ObsId = 'observation_record_id',
    UpdatedDate = 'updated_date',
    Type = 'type',
    Site = 'site',
    Values = 'values',
    PreviousRedCount = 'previous_red_count',
    RedCountChange = 'red_count_change',
    PreviousPurpleCount = 'previous_purple_count',
    PurpleCountChange = 'purple_count_change',
    PreviousGreenCount = 'previous_green_count',
    GreenCountChange = 'green_count_change',
    PreviousAmberCount = 'previous_amber_count',
    AmberCountChange = 'amber_count_change',
    XAxisChange = 'x_axis_change',
    YAxisChange = 'y_axis_change',
    YVolumeChange = 'y_volume_change',
    PanelScoreChange = 'panel_score_change',
    XAxisTitle = 'x_axis_title',
    YAxisTitle = 'y_axis_title',
    ChildPanel = 'child_panel',
    Status = 'color_direction_threshold',
    OutOfTolerance = 'oot_description',
    PreviousYValue = 'previous_y_value',
    YAxisColumnValue = 'y_axis_column_value',
    PreviousStudySiteMean = 'previous_study_site_mean',
    StudySiteMeanChange = 'study_site_mean_change',
    PreviousStudyRate = 'previous_study_rate',
    StudyRateChange = 'study_rate_change',
    PreviousXVolume = 'previous_x_volume',
    XVolumeChange = 'x_volume_change',
    ActionsCount = 'actions_count',
}

const statusRAGChips: ChipProps[] = [
    {
        text: 'Upper Red',
        value: 'Upper Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Upper Amber',
        value: 'Upper Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Amber',
        value: 'Lower Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Red',
        value: 'Lower Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Below Min Data',
        value: 'Below Min Data',
        backgroundColor: RAGPillColours.ragGrey,
        borderColor: RAGPillColours.ragGreyBorder,
    },
    {
        text: 'Green',
        value: 'Green',
        backgroundColor: RAGPillColours.ragGreen,
        borderColor: RAGPillColours.ragGreenBorder,
    },
];

const ootRAGChips: ChipProps[] = [
    {
        text: 'None',
        value: 'None',
        backgroundColor: RAGPillColours.ragNone,
        borderColor: RAGPillColours.ragNoneBorder,
    },
    {
        text: 'Upper Red',
        value: 'Upper Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Lower Red',
        value: 'Lower Red',
        backgroundColor: RAGPillColours.ragRed,
        borderColor: RAGPillColours.ragRedBorder,
    },
    {
        text: 'Upper Amber',
        value: 'Upper Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
    {
        text: 'Lower Amber',
        value: 'Lower Amber',
        backgroundColor: RAGPillColours.ragAmber,
        borderColor: RAGPillColours.ragAmberBorder,
    },
];

export class ReviewLogGridHelper implements IGridHelper {
    _isChild: boolean;

    private _customCells: CustomCells;

    constructor(
        private isChild: boolean | null | undefined,
        private customCells: CustomCells,
    ) {
        this._isChild = !!isChild;
        this._customCells = customCells;
    }

    getDisplayFields(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.CreatedBy:
            case ReviewLogColumns.CreatedDate:
            case ReviewLogColumns.Id:
            case ReviewLogColumns.ClosedDatePopulatedOn:
            case ReviewLogColumns.RecordId:
                return false;
            case ReviewLogColumns.PurpleCount:
                // This column hideColumn is designed for more bespoke hiding of columns
                // Implement any custom logic for displaying in the BE and
                // call this in any of the helpers
                return !columnType.hideColumn;
            default:
                return true;
        }
    }

    getActiveField(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.Association:
            case ReviewLogColumns.Panel:
            case ReviewLogColumns.Owner:
            case ReviewLogColumns.Decision: {
                return `${columnType.dataField}_active`;
            }
            default:
                return undefined;
        }
    }

    getDisplayTooltipFields(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.Owner:
            case ReviewLogColumns.Observation:
            case ReviewLogColumns.RootCause:
            case ReviewLogColumns.Comments:
            case ReviewLogColumns.Decision:
            case ReviewLogColumns.Association:
            case ReviewLogColumns.Panel:
            case ReviewLogColumns.XAxisTitle:
            case ReviewLogColumns.YAxisTitle:
            case ReviewLogColumns.YAxisColumnValue:
                return true;
            default:
                return false;
        }
    }

    isRagColumn(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.PreviousScore:
                return true;
            default:
                return false;
        }
    }

    disableLock(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.Panel:
            case ReviewLogColumns.Decision:
                return this._isChild;
            default:
                return false;
        }
    }

    multiValueFilter(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.PreviousScore:
            case ReviewLogColumns.PreviousXAxisValue:
            case ReviewLogColumns.PreviousYAxisValue:
            case ReviewLogColumns.PreviousYVolume:
            case ReviewLogColumns.PreviousRedCount:
            case ReviewLogColumns.PreviousAmberCount:
            case ReviewLogColumns.PreviousGreenCount:
            case ReviewLogColumns.PreviousPurpleCount:
            case ReviewLogColumns.PreviousYValue:
            case ReviewLogColumns.PreviousStudySiteMean:
            case ReviewLogColumns.PreviousStudyRate:
            case ReviewLogColumns.PreviousXVolume:
                return true;
            default:
                return false;
        }
    }

    reviewLogsColumnsWidth: Map<string | null | undefined, number> = new Map([
        [ReviewLogColumns.Type, 100],
        [ReviewLogColumns.Site, 115],
        [ReviewLogColumns.DataSet, 145],
        [ReviewLogColumns.ObsId, 100],
        [ReviewLogColumns.Association, 180],
        [ReviewLogColumns.Panel, 180],
        [ReviewLogColumns.ChildPanel, 1170],
        [ReviewLogColumns.Decision, 190],
        [ReviewLogColumns.Comments, 200],
        [ReviewLogColumns.PreviousRedCount, 150],
        [ReviewLogColumns.RedCountChange, 100],
        [ReviewLogColumns.PreviousPurpleCount, 150],
        [ReviewLogColumns.PurpleCountChange, 100],
        [ReviewLogColumns.PreviousGreenCount, 150],
        [ReviewLogColumns.GreenCountChange, 100],
        [ReviewLogColumns.PreviousAmberCount, 150],
        [ReviewLogColumns.AmberCountChange, 100],
        [ReviewLogColumns.PreviousXAxisValue, 200],
        [ReviewLogColumns.XAxisChange, 100],
        [ReviewLogColumns.PreviousYAxisValue, 200],
        [ReviewLogColumns.YAxisChange, 100],
        [ReviewLogColumns.PreviousYVolume, 200],
        [ReviewLogColumns.YVolumeChange, 100],
        [ReviewLogColumns.PreviousScore, 170],
        [ReviewLogColumns.PanelScoreChange, 100],
        [ReviewLogColumns.XAxisTitle, 180],
        [ReviewLogColumns.YAxisTitle, 180],
        [ReviewLogColumns.UpdatedDate, 145],
        [ReviewLogColumns.UpdatedBy, 145],
        [ReviewLogColumns.CreatedDate, 145],
        [ReviewLogColumns.CreatedBy, 145],
        [ReviewLogColumns.RecordId, 100],
        [ReviewLogColumns.YAxisColumnValue, 180],
        [ReviewLogColumns.PreviousStudySiteMean, 150],
        [ReviewLogColumns.StudySiteMeanChange, 100],
        [ReviewLogColumns.PreviousStudyRate, 150],
        [ReviewLogColumns.StudyRateChange, 100],
        [ReviewLogColumns.PreviousXVolume, 200],
        [ReviewLogColumns.XVolumeChange, 100],
        [ReviewLogColumns.ActionsCount, 75],
    ]);

    // We have panel column in review log parent grid and child grid both. Parent grid is displaying Panel text and child grid
    // is displaying multi-column cells inside the panel. We need to set separate width for these so adding the check here.
    getColumnWidth(columnType: ColumnDefinition): number | undefined {
        return this.reviewLogsColumnsWidth.get(
            this._isChild && columnType.dataField === ReviewLogColumns.Panel ? 'child_panel' : columnType.dataField,
        );
    }

    getCustomCell(columnType: ColumnDefinition) {
        switch (columnType.dataField) {
            case ReviewLogColumns.Status:
                return this._customCells.ChipCell(statusRAGChips, { width: 120 });
            case ReviewLogColumns.OutOfTolerance:
                return this._customCells.ChipCell(ootRAGChips, { width: 120 });
            default:
                return undefined;
        }
    }

    disableAdvancedFilter() {
        return false;
    }
}
